import {
  CAMPAIGN_GOAL_USD,
  type CampaignFixture,
  type CampaignLedger,
  type Sponsor,
  type StoryEpisode,
  type Zone,
} from './campaign'

export const fixtureZones = [
  {
    id: 'zone-01',
    number: 1,
    status: 'installed',
    roadPixelCount: 16,
    priceUsd: 16_000,
    vehicleLocation: 'REAR DRIVER DOOR',
    sponsorId: 'sponsor-northline',
  },
  {
    id: 'zone-02',
    number: 2,
    status: 'installed',
    roadPixelCount: 16,
    priceUsd: 16_000,
    vehicleLocation: 'REAR DRIVER QUARTER',
    sponsorId: 'sponsor-blue-peak',
  },
  {
    id: 'zone-03',
    number: 3,
    status: 'installed',
    roadPixelCount: 17,
    priceUsd: 17_000,
    vehicleLocation: 'FRONT DRIVER DOOR',
    sponsorId: 'sponsor-acr-partners',
  },
  {
    id: 'zone-04',
    number: 4,
    status: 'installed',
    roadPixelCount: 16,
    priceUsd: 16_000,
    vehicleLocation: 'FRONT DRIVER FENDER',
    sponsorId: 'sponsor-lumen-works',
  },
  {
    id: 'zone-05',
    number: 5,
    status: 'installed',
    roadPixelCount: 15,
    priceUsd: 15_000,
    vehicleLocation: 'REAR PASSENGER DOOR',
    sponsorId: 'sponsor-kite-studio',
  },
  {
    id: 'zone-06',
    number: 6,
    status: 'installed',
    roadPixelCount: 15,
    priceUsd: 15_000,
    vehicleLocation: 'REAR PASSENGER QUARTER',
    sponsorId: 'sponsor-field-note',
  },
  {
    id: 'zone-07',
    number: 7,
    status: 'available',
    roadPixelCount: 25,
    priceUsd: 25_000,
    vehicleLocation: 'FRONT PASSENGER DOOR',
  },
  {
    id: 'zone-08',
    number: 8,
    status: 'installed',
    roadPixelCount: 14,
    priceUsd: 14_000,
    vehicleLocation: 'FRONT PASSENGER FENDER',
    sponsorId: 'sponsor-orbit-house',
  },
  {
    id: 'zone-09',
    number: 9,
    status: 'installed',
    roadPixelCount: 12,
    priceUsd: 12_000,
    vehicleLocation: 'LIFTGATE',
    sponsorId: 'sponsor-signal-foundry',
  },
  {
    id: 'zone-10',
    number: 10,
    status: 'reserved',
    roadPixelCount: 854,
    priceUsd: 854_000,
    vehicleLocation: 'INSTALLER REVIEW RESERVE',
  },
] as const satisfies readonly Zone[]

export const fixtureSponsors = [
  {
    id: 'sponsor-northline',
    name: 'NORTHLINE',
    zoneId: 'zone-01',
    paidAmountUsd: 16_000,
    approvedDescription: 'An illustrative installed sponsor fixture.',
    installedMonthYear: 'AUG 2026',
    proofVerified: true,
  },
  {
    id: 'sponsor-blue-peak',
    name: 'BLUE PEAK',
    zoneId: 'zone-02',
    paidAmountUsd: 15_500,
    approvedDescription: 'An illustrative installed sponsor fixture.',
    installedMonthYear: 'AUG 2026',
    proofVerified: true,
  },
  {
    id: 'sponsor-acr-partners',
    name: 'ACR PARTNERS',
    zoneId: 'zone-03',
    paidAmountUsd: 17_000,
    approvedDescription:
      'The first front-runner on the Million Dollar Model X.',
    destinationUrl: 'https://example.com/sponsors/acr-partners',
    installedMonthYear: 'AUG 2026',
    proofVerified: true,
  },
  {
    id: 'sponsor-lumen-works',
    name: 'LUMEN WORKS',
    zoneId: 'zone-04',
    paidAmountUsd: 16_000,
    approvedDescription: 'An illustrative installed sponsor fixture.',
    installedMonthYear: 'AUG 2026',
    proofVerified: true,
  },
  {
    id: 'sponsor-kite-studio',
    name: 'KITE STUDIO',
    zoneId: 'zone-05',
    paidAmountUsd: 15_000,
    approvedDescription: 'An illustrative installed sponsor fixture.',
    installedMonthYear: 'AUG 2026',
    proofVerified: true,
  },
  {
    id: 'sponsor-field-note',
    name: 'FIELD NOTE',
    zoneId: 'zone-06',
    paidAmountUsd: 15_000,
    approvedDescription: 'An illustrative installed sponsor fixture.',
    installedMonthYear: 'AUG 2026',
    proofVerified: true,
  },
  {
    id: 'sponsor-orbit-house',
    name: 'ORBIT HOUSE',
    zoneId: 'zone-08',
    paidAmountUsd: 14_000,
    approvedDescription: 'An illustrative installed sponsor fixture.',
    installedMonthYear: 'AUG 2026',
    proofVerified: true,
  },
  {
    id: 'sponsor-signal-foundry',
    name: 'SIGNAL FOUNDRY',
    zoneId: 'zone-09',
    paidAmountUsd: 12_000,
    approvedDescription: 'An illustrative installed sponsor fixture.',
    installedMonthYear: 'AUG 2026',
    proofVerified: true,
  },
] as const satisfies readonly Sponsor[]

export const fixtureLedger = {
  goalUsd: CAMPAIGN_GOAL_USD,
  entries: [
    payment('payment-zone-01', 'sponsor-northline', 'zone-01', 16_000),
    payment('payment-zone-02', 'sponsor-blue-peak', 'zone-02', 16_000),
    {
      id: 'refund-zone-02-01',
      sponsorId: 'sponsor-blue-peak',
      zoneId: 'zone-02',
      kind: 'refund',
      amountUsd: -500,
      effectiveDate: '2026-08-10',
      // The fixture's $121,000 gross installed inventory is reconciled to the
      // approved $120,500 public counter through this visible correction.
      note: 'Fixture refund correction.',
    },
    payment('payment-zone-03', 'sponsor-acr-partners', 'zone-03', 17_000),
    payment('payment-zone-04', 'sponsor-lumen-works', 'zone-04', 16_000),
    payment('payment-zone-05', 'sponsor-kite-studio', 'zone-05', 15_000),
    payment('payment-zone-06', 'sponsor-field-note', 'zone-06', 15_000),
    payment('payment-zone-08', 'sponsor-orbit-house', 'zone-08', 14_000),
    payment('payment-zone-09', 'sponsor-signal-foundry', 'zone-09', 12_000),
  ],
} as const satisfies CampaignLedger

export const fixtureStoryEpisodes = [
  {
    id: 'episode-0',
    episodeNumber: 0,
    title: 'WHY THIS MODEL X?',
    body: 'This prototype explores how one privately owned Model X could become a finite map of 1,000 road pixels, paired with sponsor stories and placement proof.',
  },
] as const satisfies readonly StoryEpisode[]

export const fixtureCampaign = {
  zones: fixtureZones,
  sponsors: fixtureSponsors,
  ledger: fixtureLedger,
  storyEpisodes: fixtureStoryEpisodes,
} as const satisfies CampaignFixture

function payment(
  id: string,
  sponsorId: string,
  zoneId: string,
  amountUsd: number,
) {
  return {
    id,
    sponsorId,
    zoneId,
    kind: 'settled-payment',
    amountUsd,
    effectiveDate: '2026-08-01',
  } as const
}
